import React, { useEffect } from 'react';
import { X, Pencil, UserPlus, LogOut, Check, Users } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import type { UnifiedConversation } from '@/types/unified-messaging';
import { useAppTranslations } from '@/hooks/useAppTranslations';
import { useModalBodyFlag } from '@/hooks/useModalBodyFlag';

interface GroupInfoModalProps {
  isOpen: boolean;
  conversation: UnifiedConversation | null;
  currentUserId?: string;
  editingGroupName: boolean;
  groupNameInput: string;
  onClose: () => void;
  onEditGroupName: () => void;
  onCancelGroupNameEdit: () => void;
  onSaveGroupName: () => void;
  onGroupNameInputChange: (value: string) => void;
  onRemoveParticipant: (userId: string) => void;
  showAddParticipants: boolean;
  onShowAddParticipants: () => void;
  onCloseAddParticipants: () => void;
  participantSearchQuery: string;
  onParticipantSearchQueryChange: (query: string) => void;
  participantSearchResults: Array<{ id: string; display_name?: string; avatar_url?: string }>;
  searchingParticipants: boolean;
  selectedUsers: string[];
  onToggleUserSelection: (userId: string) => void;
  onHandleAddParticipants: () => void;
}

export function GroupInfoModal({
  isOpen,
  conversation,
  currentUserId,
  editingGroupName,
  groupNameInput,
  onClose,
  onEditGroupName,
  onCancelGroupNameEdit,
  onSaveGroupName,
  onGroupNameInputChange,
  onRemoveParticipant,
  showAddParticipants,
  onShowAddParticipants,
  onCloseAddParticipants,
  participantSearchQuery,
  onParticipantSearchQueryChange,
  participantSearchResults,
  searchingParticipants,
  selectedUsers,
  onToggleUserSelection,
  onHandleAddParticipants
}: GroupInfoModalProps) {
  const { t } = useAppTranslations();
  useModalBodyFlag(isOpen);

  if (!isOpen || !conversation || conversation.type !== 'group') return null;

  const isAdmin = conversation.created_by === currentUserId;
  const participantCount = conversation.participants?.length || 0;

  // For coaching conversations (1:1 group), show the other participant's avatar
  const isCoachingConversation = conversation.description?.startsWith('coaching:');
  const otherParticipant = isCoachingConversation
    ? conversation.participants?.find(p => p.id !== currentUserId)
    : null;
  const avatarUrl = otherParticipant?.avatar_url;

  // Generate group initials from title
  const groupInitials = (conversation.title || 'GC')
    .split(' ')
    .map(word => word[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-fade-in"
        onClick={onClose}
      />

      {/* Bottom Sheet */}
      <div className="fixed inset-x-0 bottom-0 z-50 animate-slide-up">
        <div
          className="rounded-t-3xl max-h-[85vh] overflow-hidden relative"
          style={{
            background: 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(32px) saturate(180%)',
            WebkitBackdropFilter: 'blur(32px) saturate(180%)',
            boxShadow: '0 -4px 32px rgba(15,23,42,0.16), 0 -1px 0 rgba(255,255,255,0.8) inset',
          }}
        >
          {/* Drag Handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 bg-slate-300 rounded-full" />
          </div>

          {/* Scrollable Content */}
          <div className="overflow-y-auto max-h-[calc(85vh-20px)] pb-safe">
            {/* Header with Group Avatar */}
            <div className="flex flex-col items-center px-6 pt-4 pb-6">
              {/* Group Avatar */}
              <div className="relative mb-4">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt={otherParticipant?.display_name || ''}
                    className="w-24 h-24 rounded-full object-cover shadow-lg"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)' }}>
                    <span className="text-white text-2xl font-bold">{groupInitials}</span>
                  </div>
                )}
                {!isCoachingConversation && (
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center shadow-md" style={{ background: 'rgba(255,255,255,0.95)', border: '1px solid rgba(15,23,42,0.08)' }}>
                    <Users className="w-4 h-4 text-[#2A4494]" />
                  </div>
                )}
              </div>

              {/* Group Name */}
              {editingGroupName ? (
                <div className="flex items-center gap-2 w-full max-w-xs">
                  <input
                    type="text"
                    value={groupNameInput}
                    onChange={(e) => onGroupNameInputChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onSaveGroupName();
                      if (e.key === 'Escape') onCancelGroupNameEdit();
                    }}
                    className="flex-1 px-4 py-2 text-center text-lg font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2A4494]/30"
                    style={{ background: 'rgba(255,255,255,0.8)', border: '2px solid #2A4494', boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset' }}
                    placeholder={t.messaging.enterGroupName}
                    autoFocus
                  />
                  <button
                    onClick={onSaveGroupName}
                    className="p-2 text-white rounded-xl transition-colors hover:opacity-90"
                    style={{ background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)' }}
                  >
                    <Check className="w-5 h-5" />
                  </button>
                  <button
                    onClick={onCancelGroupNameEdit}
                    className="p-2 rounded-xl transition-colors hover:bg-slate-200"
                    style={{ background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(15,23,42,0.08)' }}
                  >
                    <X className="w-5 h-5 text-slate-600" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={isAdmin ? onEditGroupName : undefined}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${isAdmin ? 'hover:bg-slate-100 cursor-pointer' : ''}`}
                  disabled={!isAdmin}
                >
                  <h2 className="text-xl font-bold text-slate-900">
                    {conversation.title || (t.messaging?.groupChatTitle ?? 'Group Chat')}
                  </h2>
                  {isAdmin && <Pencil className="w-4 h-4 text-slate-400" />}
                </button>
              )}

              {/* Participant Count */}
              <p className="text-sm text-slate-500 mt-1">
                {participantCount} {participantCount === 1 ? (t.messaging?.participantSingular ?? 'participant') : (t.messaging?.participantPlural ?? 'participants')}
              </p>
            </div>

            {/* Members Section */}
            <div className="px-6 pb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                  {t.messaging?.membersSection ?? 'Members'}
                </h3>
                {isAdmin && (
                  <button
                    onClick={onShowAddParticipants}
                    className="flex items-center gap-1.5 px-3 py-1.5 tap-target inline-flex justify-center text-[#2A4494] text-sm font-medium rounded-lg transition-colors hover:bg-[#2A4494]/10 shrink-0"
                  >
                    <UserPlus className="w-4 h-4" />
                    {t.messaging?.add ?? 'Add'}
                  </button>
                )}
              </div>

              {/* Members List */}
              <div className="rounded-2xl overflow-hidden divide-y" style={{ background: 'rgba(15,23,42,0.03)', border: '1px solid rgba(15,23,42,0.07)' }}>
                {conversation.participants?.map((participant) => (
                  <div
                    key={participant.id}
                    className="flex items-center justify-between p-4 hover:bg-slate-50/80 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10 ring-2 ring-white shadow-sm">
                        <AvatarImage src={participant.avatar_url || undefined} />
                        <AvatarFallback className="text-white text-sm font-medium" style={{ background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)' }}>
                          {participant.display_name?.[0]?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <span className="text-sm font-semibold text-slate-900 block">
                          {participant.display_name || 'Unknown User'}
                        </span>
                        {conversation.created_by === participant.id && (
                          <span className="text-xs text-[#2A4494] font-medium">{t.messaging?.adminLabel ?? 'Admin'}</span>
                        )}
                      </div>
                    </div>
                    {isAdmin && participant.id !== currentUserId && (
                      <button
                        onClick={() => onRemoveParticipant(participant.id)}
                        className="text-red-500 hover:text-red-700 text-sm font-medium px-3 py-1.5 tap-target inline-flex items-center justify-center hover:bg-red-50 rounded-lg transition-colors shrink-0"
                      >
                        {t.messaging?.remove ?? 'Remove'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Leave Group Button */}
            <div className="px-6 pb-8">
              <button
                className="w-full flex items-center justify-center gap-2 py-3 text-red-500 font-medium rounded-xl transition-colors hover:bg-red-50"
                style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.12)' }}
              >
                <LogOut className="w-5 h-5" />
                {t.messaging?.leaveGroup ?? 'Leave Group'}
              </button>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 tap-target inline-flex items-center justify-center rounded-xl transition-colors hover:bg-slate-100 shrink-0"
            style={{ background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(15,23,42,0.08)' }}
          >
            <X className="h-4 w-4 text-slate-600" />
          </button>
        </div>
      </div>

      {/* Add Participants Modal */}
      {showAddParticipants && (
        <>
          {/* Secondary Backdrop */}
          <div
            className="fixed inset-0 bg-black/30 z-[60]"
            onClick={onCloseAddParticipants}
          />

          {/* Add Participants Bottom Sheet */}
          <div className="fixed inset-x-0 bottom-0 z-[60] animate-slide-up">
            <div
              className="rounded-t-3xl max-h-[70vh] overflow-hidden"
              style={{
                background: 'rgba(255,255,255,0.96)',
                backdropFilter: 'blur(32px) saturate(180%)',
                WebkitBackdropFilter: 'blur(32px) saturate(180%)',
                boxShadow: '0 -4px 32px rgba(15,23,42,0.16), 0 -1px 0 rgba(255,255,255,0.8) inset',
              }}
            >
              {/* Drag Handle */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-6 pb-4">
                <h3 className="text-[15px] font-bold text-slate-900 tracking-tight">{t.messaging.addParticipants}</h3>
                <button
                  onClick={onCloseAddParticipants}
                  className="h-8 w-8 tap-target inline-flex items-center justify-center rounded-xl transition-colors shrink-0"
                  style={{ background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(15,23,42,0.08)' }}
                >
                  <X className="h-4 w-4 text-slate-600" />
                </button>
              </div>

              {/* Search Input */}
              <div className="px-6 pb-4">
                <input
                  type="text"
                  placeholder={t.messaging?.searchFriends ?? 'Search friends...'}
                  value={participantSearchQuery}
                  onChange={(e) => onParticipantSearchQueryChange(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2A4494]/40 placeholder-slate-400"
                  style={{ background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(15,23,42,0.10)', boxShadow: '0 1px 0 rgba(255,255,255,0.8) inset' }}
                />
              </div>

              {/* Friends / Search Results */}
              <div className="px-6 pb-6 overflow-y-auto max-h-[40vh]">
                {searchingParticipants ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <div className="w-8 h-8 border-2 border-[#2A4494] border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-slate-500 mt-3">{t.messaging?.loading ?? 'Loading...'}</p>
                  </div>
                ) : participantSearchResults.length > 0 ? (
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">
                      {participantSearchQuery ? (t.messaging?.searchResultsLabel ?? 'Search Results') : (t.messaging?.yourFriends ?? 'Your Friends')}
                    </p>
                    <div className="space-y-2">
                      {participantSearchResults.map((user) => {
                        const isSelected = selectedUsers.includes(user.id);
                        return (
                          <button
                            key={user.id}
                            onClick={() => onToggleUserSelection(user.id)}
                            className="w-full flex items-center justify-between p-4 rounded-xl transition-all"
                            style={isSelected
                              ? { background: 'rgba(42,68,148,0.08)', border: '2px solid #2A4494' }
                              : { background: 'rgba(15,23,42,0.03)', border: '1px solid rgba(15,23,42,0.07)' }
                            }
                          >
                            <div className="flex items-center gap-3">
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={user.avatar_url || undefined} />
                                <AvatarFallback className="text-white text-sm font-medium" style={{ background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)' }}>
                                  {user.display_name?.[0]?.toUpperCase() || 'U'}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-sm font-semibold text-slate-900">
                                {user.display_name || 'Unknown User'}
                              </span>
                            </div>
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center transition-colors"
                              style={isSelected
                                ? { background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)' }
                                : { background: 'rgba(15,23,42,0.08)', border: '1px solid rgba(15,23,42,0.12)' }
                              }
                            >
                              {isSelected && <Check className="w-4 h-4 text-white" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : participantSearchQuery.length >= 2 ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ background: 'rgba(15,23,42,0.05)' }}>
                      <Users className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-slate-600 font-medium">{t.messaging?.noUsersFound ?? 'No users found'}</p>
                    <p className="text-slate-400 text-xs mt-1">{t.messaging?.tryDifferentSearchTerm ?? 'Try a different search term'}</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ background: 'rgba(15,23,42,0.05)' }}>
                      <Users className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-slate-600 font-medium">{t.messaging?.noFriendsYet ?? 'No friends yet'}</p>
                    <p className="text-slate-400 text-xs mt-1">{t.messaging?.addFriendsToInvite ?? 'Add friends to invite them to groups'}</p>
                  </div>
                )}
              </div>

              {/* Add Button */}
              {selectedUsers.length > 0 && (
                <div className="px-6 pb-8 pt-2" style={{ borderTop: '1px solid rgba(15,23,42,0.07)' }}>
                  <button
                    onClick={onHandleAddParticipants}
                    className="w-full py-3.5 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
                    style={{ background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)', boxShadow: '0 4px 12px rgba(42,68,148,0.3)' }}
                  >
                    {selectedUsers.length === 1
                      ? (t.messaging?.addParticipantsOne ?? 'Add 1 Member')
                      : (t.messaging?.addParticipantsMany ?? `Add ${selectedUsers.length} Members`).replace('{count}', String(selectedUsers.length))}
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        .pb-safe {
          padding-bottom: env(safe-area-inset-bottom, 24px);
        }
      `}</style>
    </>
  );
}
